// Which instrument a pushed MIDI lane ends up playing.
//
// A note track must drive a device, and Live's own instrument cannot come
// across — so something has to stand in. Audiotool ships the General MIDI
// catalogue on its `gakki` sampler, which is a far better guess than a bare
// synth: a drum part lands on a drum kit and plays like drums, and everything
// else lands on a piano, which is the neutral "here are the notes" sound every
// producer recognises. It is a starting point, not a claim — the producer
// swaps the device in Audiotool and the reconciler never touches it again.
//
// Presets are fetched over the network, so they are resolved BEFORE the write:
// a transaction holds the document lock for its whole duration, and an await
// inside one would hold it across a round trip. Same rule the sample uploads
// follow.
//
// Nothing here is fatal. If the catalogue can't be reached — offline, or a
// token without `preset` scope — the lane gets the Heisenberg stand-in it
// always had.

import type { AudiotoolClient } from "@audiotool/nexus";

import type { PushDelta } from "../../../shared/audiotool/push-reconcile.js";
import { errorText, isSdkError } from "./uploads.js";

/** A preset for the gakki sampler, as `createDeviceFromPreset` wants it. */
export type GakkiPreset = Awaited<ReturnType<AudiotoolClient["presets"]["getInstrument"]>>;

export type PresetClient = Pick<AudiotoolClient, "presets">;

/**
 * Names that mean "this lane is percussion". Deliberately whole words: "Bassline"
 * must not match on "bass", and a lane called "Crash Test" is a rounding error
 * next to getting every "Drums", "Hats" and "808" onto a kit.
 */
const DRUM_WORDS =
  /(^|[^a-z])(drum|drums|drumkit|kit|beat|beats|perc|percussion|kick|snare|hat|hats|hihat|clap|claps|tom|toms|cymbal|ride|crash|shaker|tamb|tambourine|808|909|606|break|breaks|rhythm)([^a-z]|$)/i;

export function isDrumLane(displayName: string): boolean {
  return DRUM_WORDS.test(displayName);
}

/**
 * The preset for every note lane this delta creates, keyed by lane key.
 * A lane missing from the map falls back to the stand-in synth.
 */
export async function resolveInstruments(
  client: PresetClient,
  delta: PushDelta,
): Promise<Map<string, GakkiPreset>> {
  const lanes = delta.batches
    .flat()
    .filter((op) => op.op === "createTrack" && op.trackKind === "note")
    .map((op) => (op.op === "createTrack" ? op : null))
    .filter((op): op is Extract<typeof op, { op: "createTrack" }> => op !== null);
  const out = new Map<string, GakkiPreset>();
  if (lanes.length === 0) return out;

  // At most two fetches however many lanes there are.
  const cache = new Map<"drums" | "melodic", Promise<GakkiPreset | null>>();
  const fetchOnce = (which: "drums" | "melodic"): Promise<GakkiPreset | null> => {
    const have = cache.get(which);
    if (have) return have;
    const p = (which === "drums"
      ? client.presets.getDrums(0) // Standard Kit
      : client.presets.getInstrument(0) // Acoustic Grand Piano
    )
      // The SDK returns errors as often as it throws them; either way, no preset.
      .then((preset: unknown) => {
        if (isSdkError(preset)) throw preset;
        return preset as GakkiPreset;
      })
      .catch((e: unknown) => {
        console.warn(`[Audiotool] no GM ${which} preset (${errorText(e)}); using the stand-in synth.`);
        return null;
      });
    cache.set(which, p);
    return p;
  };

  for (const lane of lanes) {
    const preset = await fetchOnce(isDrumLane(lane.attrs.displayName) ? "drums" : "melodic");
    if (preset) out.set(lane.laneKey, preset);
  }
  return out;
}
